import React, { useState, useMemo } from "react"
import { useSelector } from "react-redux"
import {
	Button,
	Tooltip,
	notification,
	Tag,
	Progress,
	ProgressProps,
	Divider,
	Modal,
} from "antd"
import { API, Endpoints } from "utils/api"
import { getStatusLabel, getStatusColor, Status } from "utils/status"
import Strings from "utils/strings"
import { ALL_CONTRACTS_VALUE } from "hooks"
import {
	CloserOptimizerWarningModal,
	ExternalDeliverySplitModal,
} from "./modals"
import type { ExternalDeliveryMode } from "./modals"

interface ChangeStatusButtonProps {
	orders: any[] // Support both single order (array of 1) and multiple orders
	setOrders: React.Dispatch<React.SetStateAction<any[]>>
	waitForModalConfirmation: (nextStatus: string) => Promise<boolean> // Function to show confirmation modal and wait for user response
}

const ChangeStatusButton: React.FC<ChangeStatusButtonProps> = ({
	orders,
	setOrders,
	waitForModalConfirmation,
}) => {
	const [loading, setLoading] = useState(false)
	const [warningModal, setWarningModal] = useState<{
		visible: boolean
		mismatchedOrders: any[]
		resolve: ((includedIds: string[]) => void) | null
		reject: (() => void) | null
	}>({ visible: false, mismatchedOrders: [], resolve: null, reject: null })
	const [externalModal, setExternalModal] = useState<{
		visible: boolean
		eligible: any[]
		ineligible: any[]
		total: number
		resolve: ((mode: ExternalDeliveryMode) => void) | null
		reject: (() => void) | null
	}>({
		visible: false,
		eligible: [],
		ineligible: [],
		total: 0,
		resolve: null,
		reject: null,
	})

	// Redux variables
	const { business, contract, contractDetails } =
		useSelector((state: any) => state.filters) || {}
	const contractType = contractDetails?.type
	// "All contracts" (left over from the Routes screen, since this filter is
	// shared across screens) has no single contract to scope the update to.
	const isAllContracts = contract === ALL_CONTRACTS_VALUE

	// Check if all orders have the same status
	const commonStatus = useMemo(() => {
		if (orders.length === 0) return null
		const firstStatus = orders[0].orderFlow.status
		const allSameStatus = orders.every(
			(order) => order.orderFlow.status === firstStatus
		)
		return allSameStatus ? firstStatus : null
	}, [orders])

	// Define status flow for orders
	const getNextStatus = (currentStatus: string): string | null => {
		const statusFlow: Record<string, string> = {
			address_confirmation: "picking",
			picking: "packing",
			packing: "processing",
		}
		return statusFlow[currentStatus] || null
	}

	const nextStatus = commonStatus ? getNextStatus(commonStatus) : null

	// Check if any order has errors when moving to picking
	const hasOrderErrors = useMemo(() => {
		if (nextStatus !== "picking") return false
		return orders.some((order) =>
			Object.values(order.orderErrors || {}).some(Boolean)
		)
	}, [nextStatus, orders])

	// Check if packing orders have valid boxes/bags
	const canAggregate = useMemo(() => {
		if (commonStatus !== "packing") return true

		const ordersWithBoxes = orders.filter(
			(order) => (order.orderFlow?.boxes || []).length > 0
		)
		const ordersWithBags = orders.filter((order) => order.orderFlow?.bagId)

		if (contractType === "parcel") {
			return (
				(ordersWithBags.length > 0 || ordersWithBoxes.length > 0) &&
				ordersWithBags.length + ordersWithBoxes.length === orders.length
			)
		}

		return (
			ordersWithBoxes.length > 0 && ordersWithBoxes.length === orders.length
		)
	}, [commonStatus, orders, contractType])

	// Check if any packing orders have a delivery date in the past
	const hasOldOrders = useMemo(() => {
		if (commonStatus !== "packing") return false
		const today = new Date()
		today.setHours(0, 0, 0, 0)
		return orders.some(
			(order) =>
				order.orderFlow?.deliveryDate &&
				new Date(order.orderFlow.deliveryDate) < today
		)
	}, [commonStatus, orders])

	// Determine if status change is allowed
	const canChangeStatus = useMemo(() => {
		if (isAllContracts) return false
		if (!commonStatus || !nextStatus) return false
		if (hasOrderErrors) return false
		if (commonStatus === "packing" && (!canAggregate || hasOldOrders))
			return false
		return true
	}, [
		isAllContracts,
		commonStatus,
		nextStatus,
		hasOrderErrors,
		canAggregate,
		hasOldOrders,
	])

	// Check if non-parcel orders have bags (not allowed)
	const hasBagsInNonParcel = useMemo(() => {
		if (commonStatus !== "packing" || contractType === "parcel") return false
		return orders.some((order) => order.orderFlow?.bagId)
	}, [commonStatus, orders, contractType])

	// Validation errors for tooltip
	const validationErrors = useMemo(() => {
		if (isAllContracts) {
			return [Strings.routes.contractRequiredForAction]
		}
		if (!commonStatus) {
			return [Strings.orders.allOrdersSameStatus]
		}
		if (!nextStatus) {
			return [Strings.orders.noNextStatus]
		}
		if (hasOrderErrors) {
			return [Strings.orders.ordersWithErrorsCannotBeSentToPicking]
		}
		if (commonStatus === "packing" && (!canAggregate || hasOldOrders)) {
			const errors: string[] = []
			if (hasOldOrders) {
				errors.push(Strings.orders.ordersInThePastCannotBeSent)
			}
			if (!canAggregate) {
				if (hasBagsInNonParcel) {
					errors.push(Strings.orders.bagsNotAllowedInNonParcel)
				}
				errors.push(
					contractType === "parcel"
						? Strings.orders.allOrdersMustHaveBoxesOrBags
						: Strings.orders.allOrdersMustHaveBoxes
				)
			}
			return errors
		}
		return []
	}, [
		isAllContracts,
		commonStatus,
		nextStatus,
		hasOrderErrors,
		canAggregate,
		hasOldOrders,
		hasBagsInNonParcel,
		contractType,
	])

	const handleChangeStatus = async () => {
		if (!commonStatus || !canChangeStatus || !nextStatus) return

		if (commonStatus === "address_confirmation") {
			const confirmed = await waitForModalConfirmation(nextStatus)
			if (!confirmed) return
		}

		// Handle optimizer call for packing status
		if (commonStatus === "packing") {
			let ordersToSend = orders

			if (contractDetails?.optimizer === "closer") {
				const mismatched = orders.filter((order) => {
					const slot = order.slot
					const shift = order.orderFlow.shift
					if (!slot || !shift) return false
					return slot.from !== shift.from || slot.to !== shift.to
				})

				if (mismatched.length > 0) {
					const includedMismatchedIds = await new Promise<string[]>(
						(resolve, reject) => {
							setWarningModal({
								visible: true,
								mismatchedOrders: mismatched,
								resolve,
								reject,
							})
						}
					).catch(() => null)

					if (includedMismatchedIds === null) return

					const mismatchedIds = new Set(mismatched.map((o) => o._id))
					const includedSet = new Set(includedMismatchedIds)
					ordersToSend = orders.filter(
						(o) => !mismatchedIds.has(o._id) || includedSet.has(o._id)
					)
				}
			}

			const allOrderIds = ordersToSend.map((order) => order._id)
			let orderIds = allOrderIds
			let externalDeliveryMode: ExternalDeliveryMode | null = null
			let externalOrderIds: string[] = []

			// Contracts with an external-delivery partner active let ops decide whether
			// the eligible orders get their own bike-only optimizer run.
			if (contractDetails?.allowExternalDelivery && allOrderIds.length > 0) {
				setLoading(true)
				const eligibilityResponse = await API.post({
					url: Endpoints.v2.uriOrderExternalDeliveryEligibility(),
					data: { orders: allOrderIds },
					headers: { business, contract },
				})
				setLoading(false)

				if (!eligibilityResponse.ok) {
					notification.error({
						message: Strings.sidebar.orders,
						description:
							eligibilityResponse?.data?.message ||
							Strings.orders.externalSplitEligibilityFailed,
						placement: "bottomRight",
						duration: 5,
					})
					return
				}

				const { eligible = [], ineligible = [] } =
					eligibilityResponse.data?.results || {}

				if (eligible.length > 0) {
					const chosenMode = await new Promise<ExternalDeliveryMode>(
						(resolve, reject) => {
							setExternalModal({
								visible: true,
								eligible,
								ineligible,
								total: allOrderIds.length,
								resolve,
								reject,
							})
						}
					).catch(() => null)

					if (chosenMode === null) return

					if (chosenMode !== "all_internal") {
						externalDeliveryMode = chosenMode
						externalOrderIds = eligible.map((o: any) => String(o._id))
						// Only the orders the chosen mode actually sends leave packing, so
						// only those may be optimistically removed from the list below.
						if (chosenMode === "external_only") {
							orderIds = externalOrderIds
						} else if (chosenMode === "non_external_only") {
							orderIds = allOrderIds.filter(
								(id) => !externalOrderIds.includes(String(id))
							)
						}
					}
				}
			}

			setLoading(true)

			try {
				const apiPromise = API.patch({
					url: Endpoints.uriOrders("status"),
					data: {
						status: "packing",
						orders: allOrderIds,
						aggregationEnded: false,
						...(externalDeliveryMode
							? { externalDeliveryMode, externalOrders: externalOrderIds }
							: {}),
					},
					headers: {
						business,
						contract,
					},
					// A split runs two optimizer passes back to back, so the window has
					// to cover both.
					axiosConfig: { timeout: 1800000 },
				})

				const fallbackTimeout = setTimeout(() => {
					notification.success({
						message: Strings.sidebar.orders,
						description: Strings.orders.sendToOptimizerToCreateRoutes.replace(
							"{0}",
							orderIds.length.toString()
						),
						placement: "bottomRight",
						duration: 5,
					})

					setOrders((prevOrders) =>
						prevOrders.filter((order) => !orderIds.includes(order._id))
					)

					setLoading(false)
				}, 2000)

				apiPromise
					.then((response) => {
						if (!response.ok) {
							clearTimeout(fallbackTimeout)
							const errorMsg =
								response?.data?.message || Strings.serverErrors.wentWrong
							const missingOrders = response?.data?.results?.orders
							Modal.error({
								title: Strings.sidebar.orders,
								content: missingOrders?.length ? (
									<div>
										<p>{errorMsg}</p>
										<ul
											style={{
												paddingLeft: 20,
												maxHeight: 200,
												overflowY: "auto",
											}}
										>
											{missingOrders.map((o: any, i: number) => (
												<li key={i}>
													{o.dingoo_order_id ||
														o.order_id ||
														o.optimizer_order_id}
												</li>
											))}
										</ul>
									</div>
								) : (
									errorMsg
								),
							})
						}
					})
					.catch((error) => {
						clearTimeout(fallbackTimeout)
						Modal.error({
							title: Strings.sidebar.orders,
							content:
								error?.response?.data?.message ||
								Strings.serverErrors.wentWrong,
						})
					})
					.finally(() => {
						setLoading(false)
					})
			} catch (err) {
				notification.error({
					message: Strings.serverErrors.title,
					description: Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
				setLoading(false)
			}

			return
		}

		setLoading(true)

		try {
			const orderIds = orders.map((order) => order._id)

			const response = await API.patch({
				url: Endpoints.uriOrders("status"),
				data: {
					status: commonStatus,
					orders: orderIds,
				},
				headers: {
					business,
					contract,
				},
			})

			if (response.ok) {
				// Update local state with new status
				setOrders((prevOrders) => {
					return prevOrders.map((order) => {
						if (!orderIds.includes(order._id)) return order
						return {
							...order,
							orderFlow: {
								...order.orderFlow,
								status: nextStatus,
							},
						}
					})
				})

				notification.success({
					message: Strings.orders.statusUpdated,
					description:
						orders.length === 1
							? Strings.orders.changeStatusTo.replace(
									"{0}",
									getStatusLabel(nextStatus as Status)
								)
							: Strings.orders.changeMultipleOrdersStatus
									.replace("{0}", orders.length.toString())
									.replace("{1}", getStatusLabel(nextStatus as Status)),
					placement: "bottomRight",
					duration: 3,
				})
			} else {
				notification.error({
					message: Strings.orders.updateFailed,
					description:
						response?.data?.message || Strings.orders.statusUpdateError,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.orders.updateFailed,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		} finally {
			setLoading(false)
		}
	}

	// No orders selected or status has no next step
	if (orders.length === 0 || (commonStatus && !nextStatus)) return null

	const isDisabled = !canChangeStatus
	const currentStatusColor = commonStatus
		? getStatusColor(commonStatus as Status)
		: undefined
	const nextStatusColor = nextStatus
		? getStatusColor(nextStatus as Status)
		: undefined

	const gradientStatusColors: ProgressProps["strokeColor"] =
		currentStatusColor && nextStatusColor
			? { "0%": currentStatusColor, "100%": nextStatusColor }
			: undefined

	const isPacking = commonStatus === "packing"

	const tooltipTitle = isDisabled ? (
		<div>
			<div style={{ fontWeight: "bold", marginBottom: 4 }}>
				{Strings.orders.statusChangeRequirements}
			</div>
			<ul style={{ margin: 0, paddingLeft: 20 }}>
				{validationErrors.map((error, index) => (
					<li key={index}>{error}</li>
				))}
			</ul>
		</div>
	) : isPacking ? (
		Strings.orders.sendToOptimizerToCreateRoutes.replace(
			"{0}",
			orders.length.toString()
		)
	) : orders.length === 1 ? (
		Strings.orders.changeStatusTo.replace(
			"{0}",
			getStatusLabel(nextStatus as Status)
		)
	) : (
		Strings.orders.changeMultipleOrdersStatus
			.replace("{0}", orders.length.toString())
			.replace("{1}", getStatusLabel(nextStatus as Status))
	)

	return (
		<div className="w-100">
			<Tooltip title={tooltipTitle}>
				<Button
					type="text"
					onClick={isDisabled ? undefined : handleChangeStatus}
					style={{
						width: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						gap: 6,
						padding: "0px",
						height: "auto",
						border: "none",
						background: "transparent",
						opacity: isDisabled ? 0.5 : 1,
						cursor: isDisabled ? "not-allowed" : "pointer",
					}}
				>
					<Tag
						color={currentStatusColor || "default"}
						style={{
							margin: 0,
							fontSize: 12,
							fontWeight: 500,
							opacity: isDisabled ? 0.8 : 1,
						}}
					>
						{commonStatus
							? getStatusLabel(commonStatus as Status)
							: Strings.orders.multipleStatuses}
					</Tag>
					<svg
						width="14"
						height="14"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						style={{
							color: isDisabled ? "#999" : "#1890ff",
							margin: "0 10px",
						}}
					>
						<path
							d="M5 12H19M19 12L12 5M19 12L12 19"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
					<Tag
						color={nextStatusColor || "default"}
						style={{
							margin: 0,
							fontSize: 12,
							fontWeight: 500,
							opacity: isDisabled ? 0.8 : 1,
						}}
					>
						{nextStatus ? getStatusLabel(nextStatus as Status) : "?"}
					</Tag>
				</Button>
			</Tooltip>

			{loading ? (
				<Progress
					percent={100}
					status="active"
					showInfo={false}
					strokeColor={gradientStatusColors}
					style={{
						opacity: 0.5,
						transition: "opacity 0.3s ease-in-out",
						margin: "6px 0",
					}}
				/>
			) : (
				<Divider style={{ margin: "16px 0" }} />
			)}

			<CloserOptimizerWarningModal
				visible={warningModal.visible}
				mismatchedOrders={warningModal.mismatchedOrders}
				totalOrders={orders.length}
				onConfirm={(selectedIds) => {
					warningModal.resolve?.(selectedIds)
					setWarningModal((s) => ({ ...s, visible: false }))
				}}
				onCancel={() => {
					warningModal.reject?.()
					setWarningModal((s) => ({ ...s, visible: false }))
				}}
			/>

			<ExternalDeliverySplitModal
				visible={externalModal.visible}
				eligibleOrders={externalModal.eligible}
				ineligibleOrders={externalModal.ineligible}
				totalOrders={externalModal.total}
				submitting={loading}
				onConfirm={(mode) => {
					externalModal.resolve?.(mode)
					setExternalModal((s) => ({ ...s, visible: false }))
				}}
				onCancel={() => {
					externalModal.reject?.()
					setExternalModal((s) => ({ ...s, visible: false }))
				}}
			/>
		</div>
	)
}

export default ChangeStatusButton
